import subMonths from 'date-fns/subMonths';
import addMonths from 'date-fns/addMonths';
import isPast from 'date-fns/isPast';
import parseISO from 'date-fns/parseISO';
import compareDesc from 'date-fns/compareDesc';
import compareAsc from 'date-fns/compareAsc';
import { ColDef, ColGroupDef } from '@ag-grid-community/core';
import { ICalendarKeys } from '../index';

import { injectQuotes } from '../../lib/utils';
import CalendarService from '../../lib/services/CalendarService';
import { Dividends, Earnings, AnalystRating, Options, IPO } from './index';
import { CalendarManager, ISetParams, setParams } from '@benzinga/calendar-manager';
import { getSessionSingleton } from '@benzinga/session';

const Calendar = new CalendarService();

export interface IFetchParams {
  symbols?: string[];
  dateFrom?: Date;
  dateTo?: Date;
  pageSize?: number;
}

export type CalendarTab = 'Earnings' | 'Analyst Ratings' | 'Options' | 'Dividends' | 'IPOs';

export interface ICalendarDef {
  key: ICalendarKeys;
  link: string;
  tab: CalendarTab;
  columns: (ColDef | ColGroupDef)[];
  edgeAdKey?: string;
  fetch(params: IFetchParams): Promise<any>;
}

const sliceAndGroupData = (items: any[], dateField = 'date', sort = 'asc'): any[] => {
  const groupBy = (xs: any[], key: string) => {
    return xs.reduce((rv, x) => {
      (rv[x[key]] = rv[x[key]] || []).push(x);
      return rv;
    }, {});
  };

  let sortByDate = (a: any, b: any) => {
    return compareAsc(parseISO(a[dateField]), parseISO(b[dateField]));
  };

  if (sort === 'desc') {
    sortByDate = (a: any, b: any) => {
      return compareDesc(parseISO(a[dateField]), parseISO(b[dateField]));
    };
  }

  const grouped = groupBy(items, 'ticker');
  const separated = {};
  let res: any[] = [];

  for (const ticker in grouped) {
    separated[ticker] = {
      future: [],
      past: [],
    };
    grouped[ticker].forEach((el: any) => {
      const date = parseISO(el[dateField]);
      if (isPast(date)) {
        separated[ticker].past.push(el);
      } else {
        separated[ticker].future.push(el);
      }
    });

    const pastSliced = separated[ticker].past.slice(0, 3);
    const futureSliced = separated[ticker].future.slice(0, 2);
    res = res.concat([...pastSliced, ...futureSliced]);
  }

  return res.sort(sortByDate);
};

const CalendarDef: ICalendarDef[] = [
  {
    columns: Earnings,
    edgeAdKey: 'earnings',
    fetch: (params: IFetchParams = {}) =>
      Calendar.getEarnings(
        setParams({
          dateFrom: params.dateFrom ? params.dateFrom : subMonths(new Date(), 1),
          dateTo: params.dateTo ? params.dateTo : new Date(),
          ...params,
        }),
        //setParams({ dateFrom: subMonths(new Date(), 3), dateTo: addMonths(new Date(), 1), ...params }),
      ).then(({ data }: any) => {
        const response: { data: { earnings: any[] } } = {
          data: {
            earnings: [],
          },
        };

        if (Array.isArray(data.earnings) && data.earnings.length) {
          response.data.earnings = sliceAndGroupData(data.earnings);
          const sortByRevenueEst = (a: any, b: any) =>
            b.date < a.date ? -1 : 1 || (Number(a.revenue_est ?? 0) < Number(b.revenue_est ?? 0) ? 1 : -1);
          response.data.earnings.sort(sortByRevenueEst);
          return response;
        }
        return response;
      }),
    key: 'earnings',
    link: 'https://www.benzinga.com/earnings',
    tab: `Earnings`,
  },
  {
    columns: AnalystRating,
    edgeAdKey: 'analyst-ratings',
    fetch: (params: IFetchParams = {}) => {
      const query: ISetParams = {
        dateFrom: subMonths(new Date(), 6),
        dateTo: new Date(),
        pageSize: params.pageSize,
        symbols: params.symbols,
      };

      const session = getSessionSingleton();
      const calendarManager = session.getManager(CalendarManager);
      return calendarManager.getCalendarData('ratings', query).then(res => {
        const ratings = res.ok;

        const response: { data: { ratings: any[] } } = {
          data: {
            ratings: [],
          },
        };

        if (Array.isArray(ratings) && ratings.length) {
          return injectQuotes(ratings).then((res: any) => {
            response.data.ratings = sliceAndGroupData(res, 'date', 'desc');
            return response;
          });
          // This is for if we can get await to work via babel update
          // transformedData = await injectQuotes(transformedData);
          // response.data.ratings = transformedData;
          // return response;
        }
        return response;
      });
    },
    key: 'ratings',
    link: 'https://www.benzinga.com/analyst-stock-ratings',
    tab: `Analyst Ratings`,
  },
  {
    columns: Options,
    edgeAdKey: 'unusual-options-activity',
    fetch: (params: IFetchParams = {}) =>
      Calendar.getOptionActivitySignals(
        setParams({ dateFrom: subMonths(new Date(), 1), dateTo: addMonths(new Date(), 1), ...params }),
      ).then(({ data }: any) => {
        const response: { data: { option_activity: any[] } } = {
          data: {
            option_activity: [],
          },
        };

        if (Array.isArray(data.option_activity) && data.option_activity.length) {
          response.data.option_activity = sliceAndGroupData(data.option_activity);
          return response;
        }
        return response;
      }),
    key: 'option_activity',
    link: 'https://www.benzinga.com/calendars/options',
    tab: `Options`,
  },
  {
    columns: Dividends,
    edgeAdKey: 'basic-calendar',
    fetch: (params: IFetchParams = {}) =>
      Calendar.getDividends(
        setParams({
          dateFrom: new Date(),
          dateSort: 'ex',
          dateTo: params.symbols?.length ? addMonths(new Date(), 3) : new Date(),
          ...params,
        }),
      ).then(({ data }: any) => {
        const response: { data: { dividends: any[] } } = {
          data: {
            dividends: [],
          },
        };

        if (Array.isArray(data.dividends) && data.dividends.length) {
          response.data.dividends = sliceAndGroupData(data.dividends, 'ex_dividend_date');
          return response;
        }
        return response;
      }),
    key: 'dividends',
    link: 'https://www.benzinga.com/dividends',
    tab: `Dividends`,
  },
  {
    columns: IPO,
    edgeAdKey: 'basic-calendar',
    fetch: (params: IFetchParams = {}) =>
      Calendar.getIPOs(setParams({ dateFrom: new Date(), dateTo: addMonths(new Date(), 1), ...params })),
    key: 'ipos',
    link: 'https://www.benzinga.com/calendars/ipos',
    tab: `IPOs`,
  },
  // {
  //   columns: Economics,
  //   fetch: () => Calendar.getEconomics(setParams({ dateFrom: subDays(new Date(), 7), dateTo: new Date() })),
  //   key: 'economics',
  //   link: 'https://www.benzinga.com/calendars/economic',
  //   tab: `Economic`,
  // },
  // {
  //   columns: StockSplit,
  //   fetch: (params: IFetchParams = {}) =>
  //     Calendar.getSplits(
  //       setParams({
  //         dateFrom: new Date(),
  //         dateTo: addMonths(new Date(), 1),
  //         ...params,
  //         addParams: { key: 'parameters[date_sort]', val: 'ex' },
  //       }),
  //     ),
  //   key: 'splits',
  //   link: 'https://www.benzinga.com/calendars/stock-splits',
  //   tab: `Stock Split`,
  // },
  // {
  //   columns: ConferenceCall,
  //   fetch: (params: IFetchParams = {}) =>
  //     Calendar.getConferenceCalls(setParams({ dateFrom: new Date(), dateTo: addMonths(new Date(), 1), ...params })),
  //   key: 'conference',
  //   link: 'https://www.benzinga.com/calendars/conference-calls',
  //   tab: `Conference Call`,
  // },
  // {
  //   columns: MA,
  //   fetch: (params: IFetchParams = {}) =>
  //     Calendar.getMA(setParams({ dateFrom: subMonths(new Date(), 2), dateTo: new Date(), ...params })),
  //   key: 'ma',
  //   link: 'https://www.benzinga.com/calendars/m-a',
  //   tab: `M&A`,
  // },
];

export default CalendarDef;
